// components/DateTimePickerComponent/index.tsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import {
    ChevronLeft,
    ChevronRight,
    XLg
} from "react-bootstrap-icons";
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    isToday,
    setHours,
    setMinutes,
    parseISO,
    isValid
} from 'date-fns';
import { calender_icon } from "../../assets/images";
import "./styles.css";

interface DateTimePickerProps {
    placeholder?: string;
    label: string;
    dateValue: string | null;
    timeValue: string | null;
    onDateChange: (date: string | null) => void;
    onTimeChange: (time: string | null) => void;
    className?: string;
    minDate?: Date | null;
    maxDate?: Date | null;
    minTime?: string | null;
    maxTime?: string | null;
    showTodayPickupMessage?: boolean;
    autoScrollToFirstAvailableTime?: boolean;
}

const DateTimePickerComponent = ({
    placeholder,
    label,
    dateValue,
    timeValue,
    onDateChange,
    onTimeChange,
    className,
    minDate,
    maxDate,
    minTime,
    maxTime,
    showTodayPickupMessage,
    autoScrollToFirstAvailableTime,
}: DateTimePickerProps) => {
    const [showModal, setShowModal] = useState(false);
    const [viewDate, setViewDate] = useState(dateValue ? new Date(dateValue) : new Date());
    const [tempDate, setTempDate] = useState<Date | null>(dateValue ? new Date(dateValue) : null);
    const [tempTime, setTempTime] = useState<string | null>(timeValue);
    const timeGridRef = useRef<HTMLDivElement>(null);

    // Sync state when props change or modal opens
    useEffect(() => {
        if (showModal) {
            setTempDate(dateValue ? new Date(dateValue) : null);
            setTempTime(timeValue);
            if (dateValue) {
                setViewDate(new Date(dateValue));
            }
        }
    }, [showModal, dateValue, timeValue]);

    // Scroll time grid to first available slot — only when explicitly enabled (pickup picker)
    useEffect(() => {
        if (!autoScrollToFirstAvailableTime || !showModal) return;
        // Only scroll when the selected date is today (past slots exist)
        if (!tempDate || !isToday(tempDate)) return;
        const timer = setTimeout(() => {
            if (!timeGridRef.current) return;
            const firstAvailable = timeGridRef.current.querySelector<HTMLButtonElement>(
                '.dt-time-btn:not(.is-past):not([disabled])'
            );
            if (firstAvailable) {
                firstAvailable.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [autoScrollToFirstAvailableTime, showModal, tempDate]);

    const handleOpen = () => setShowModal(true);
    const handleClose = () => setShowModal(false);

    const handleConfirm = () => {
        if (tempDate) {
            const year = tempDate.getFullYear();
            const month = String(tempDate.getMonth() + 1).padStart(2, '0');
            const day = String(tempDate.getDate()).padStart(2, '0');
            const dateString = `${year}-${month}-${day}`;

            onDateChange(dateString);
            onTimeChange(tempTime);
        }
        handleClose();
    };

    const handleMonthPrev = () => setViewDate(subMonths(viewDate, 1));
    const handleMonthNext = () => setViewDate(addMonths(viewDate, 1));

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            handleClose();
        }
    };

    const days = useMemo(() => {
        const start = startOfWeek(startOfMonth(viewDate));
        const end = endOfWeek(endOfMonth(viewDate));
        return eachDayOfInterval({ start, end });
    }, [viewDate]);

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const timeSlots = useMemo(() => {
        const now = new Date();
        const selectedIsToday = tempDate ? isToday(tempDate) : false;
        const isMinDay = minDate && tempDate && isSameDay(tempDate, minDate);
        const isMaxDay = maxDate && tempDate && isSameDay(tempDate, maxDate);

        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        const slots = [];
        for (let h = 9; h <= 22; h++) {
            for (let m = 0; m < 60; m += 15) {
                if (h === 22 && m > 0) break;

                const displayTime = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                const value = displayTime;

                let isPastSlot = selectedIsToday &&
                    (h < currentHour || (h === currentHour && m <= currentMinute));

                // Check minTime if on minDate
                if (!isPastSlot && isMinDay && minTime) {
                    if (value <= minTime) isPastSlot = true;
                }

                // Check maxTime if on maxDate
                if (!isPastSlot && isMaxDay && maxTime) {
                    if (value >= maxTime) isPastSlot = true;
                }

                slots.push({ displayTime, value, isPast: isPastSlot });
            }
        }
        return slots;
    }, [tempDate, minDate, maxDate, minTime, maxTime]);

    const selectedDateDisplay = tempDate ? format(tempDate, 'dd MMM yyyy') : 'No Date Selected';

    const getTodayPickupMessage = () => {
        if (!showTodayPickupMessage) return null;

        const today = new Date();
        const isTodayBeforeMin = minDate ? (today < minDate && !isSameDay(today, minDate)) : false;
        const isTodayAfterMax = maxDate ? (today > maxDate && !isSameDay(today, maxDate)) : false;
        const isTodayDisabled = isTodayBeforeMin || isTodayAfterMax;

        if (tempDate && isToday(tempDate)) {
            const selectableSlots = timeSlots.filter(slot => !slot.isPast);
            if (selectableSlots.length > 0) {
                const firstTime = selectableSlots[0].displayTime;
                return `* Today services are available between ${firstTime} to 22:00`;
            } else {
                return "* No services today";
            }
        }

        if (isTodayDisabled) {
            return "* No services today";
        }

        return null;
    };

    const displayValue = useMemo(() => {
        if (!dateValue) return "";
        try {
            const d = new Date(dateValue);
            if (!isValid(d)) return "";
            const datePart = format(d, 'dd-MM-yyyy');
            if (!timeValue) return datePart;
            return `${datePart} ${timeValue}`;
        } catch (e) {
            return "";
        }
    }, [dateValue, timeValue]);

    return (
        <div className={`date-time-component-container ${className || ""}`}>
            <div className="date-time-component-wrapper" onClick={handleOpen} role="button" tabIndex={0} onKeyDown={handleKeyDown}>
                <div className="icon-input-container">
                    <img
                        src={calender_icon}
                        alt="Calendar Icon"
                        className="me-1"
                        style={{ cursor: "pointer", height: "20px", minWidth: "20px" }}
                    />
                    <div className="text-start w-100">
                        <label className="label-date-time">{label}</label>
                        <input
                            readOnly
                            onFocus={(e) => e.target.blur()}
                            value={displayValue}
                            placeholder={placeholder || "Select date & time"}
                            className="input-date w-100"
                            onClick={handleOpen}
                            aria-label={`${label} selector`}
                            type="text"
                        />
                    </div>
                </div>
            </div>

            <Modal
                show={showModal}
                onHide={handleClose}
                centered
                dialogClassName="custom-datetime-modal"
                contentClassName="custom-datetime-content"
                onKeyDown={handleKeyDown}
                size="lg"
            >
                <div className="custom-dt-header">
                    <h5 className="custom-dt-title">Select {label.toLowerCase()}</h5>
                    <button
                        className="custom-dt-close"
                        onClick={handleClose}
                        aria-label="Close dialog"
                        type="button"
                    >
                        <XLg size={18} />
                    </button>
                </div>

                <div className="custom-dt-body">
                    <div className="custom-dt-calendar-section">
                        <div className="custom-dt-month-nav">
                            <span className="custom-dt-current-month">{format(viewDate, 'MMMM yyyy')}</span>
                            <div className="custom-dt-nav-btns">
                                <button
                                    onClick={handleMonthPrev}
                                    aria-label="Previous month"
                                    type="button"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    onClick={handleMonthNext}
                                    aria-label="Next month"
                                    type="button"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="custom-dt-calendar-grid">
                            {weekDays.map(day => (
                                <div key={day} className="custom-dt-weekday">{day}</div>
                            ))}
                            {days.map((day, idx) => {
                                const isCurrentMonth = isSameMonth(day, viewDate);
                                const isSelected = tempDate && isSameDay(day, tempDate);

                                const isPastDate = day < new Date() && !isToday(day);
                                const isBeforeMin = minDate ? (day < minDate && !isSameDay(day, minDate)) : false;
                                const isAfterMax = maxDate ? (day > maxDate && !isSameDay(day, maxDate)) : false;

                                const isDisabled = isPastDate || isBeforeMin || isAfterMax;

                                return (
                                    <button
                                        key={idx}
                                        className={`custom-dt-day 
                                            ${!isCurrentMonth ? 'is-outside' : ''} 
                                            ${isSelected ? 'is-selected' : ''} 
                                            ${isToday(day) ? 'is-today' : ''}
                                            ${isDisabled ? 'is-past' : ''}
                                        `}
                                        onClick={() => !isDisabled && setTempDate(day)}
                                        disabled={isDisabled}
                                        aria-label={format(day, 'MMMM d, yyyy')}
                                        aria-pressed={isSelected ? true : undefined}
                                        type="button"
                                    >
                                        {format(day, 'd')}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="custom-dt-time-section">
                        <div className="dt-info-box">
                            <label>Selected Date & Time</label>
                            <div className="dt-info-value">
                                {selectedDateDisplay}
                                {tempTime && <span className="dt-time-display">{tempTime}</span>}
                            </div>
                        </div>

                        <div className="dt-time-picker-box">
                            <label>Select Time</label>
                            {(() => {
                                const msg = getTodayPickupMessage();
                                if (!msg) return null;
                                const isNoService = msg.includes("No services today");
                                return (
                                    <div className={`dt-pickup-info-msg ${isNoService ? 'no-service' : 'service-available'}`}>
                                        {msg}
                                    </div>
                                );
                            })()}
                            <div className="dt-time-grid" ref={timeGridRef}>
                                {timeSlots.map(slot => (
                                    <button
                                        key={slot.value}
                                        className={`dt-time-btn ${tempTime === slot.value ? 'is-selected' : ''} ${slot.isPast ? 'is-past' : ''}`}
                                        onClick={() => !slot.isPast && setTempTime(slot.value)}
                                        disabled={slot.isPast}
                                        aria-pressed={tempTime === slot.value ? true : undefined}
                                        type="button"
                                    >
                                        {slot.displayTime}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="custom-dt-footer">
                    <Button
                        variant="link"
                        className="custom-dt-cancel"
                        onClick={handleClose}
                        type="button"
                    >
                        Cancel
                    </Button>
                    <Button
                        className="custom-dt-confirm"
                        onClick={handleConfirm}
                        type="button"
                    >
                        Confirm Date & Time
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default DateTimePickerComponent;
